/**
 * Feature Request Slice
 * Redux Toolkit slice for feature request state management
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import type {
  FeatureRequest,
  FeatureRequestStatus,
  CreateFeatureRequestRequest,
  UpdateFeatureRequestStatusRequest,
} from '@creatoros/shared';
import { featureRequestService } from '../../services/feature-request.service';
import { ApiErrorState } from '../../types/error.types';
import { logger } from '../../utils/logger';

/**
 * Feature Request State Interface
 */
export interface FeatureRequestState {
  // Public feature requests (approved)
  publicRequests: FeatureRequest[];
  // User's own requests (all statuses)
  myRequests: FeatureRequest[];
  // Admin view (all requests)
  adminRequests: FeatureRequest[];
  // Currently selected request
  selectedRequest: FeatureRequest | null;
  // User's votes: featureRequestId -> voteType
  userVotes: Record<string, 'up' | 'down'>;
  // Loading states
  isLoading: boolean;
  isSubmitting: boolean;
  isVoting: boolean;
  isUpdatingStatus: boolean;
  // Error state
  error: ApiErrorState | null;
  // Pagination
  publicContinuationToken?: string;
  myContinuationToken?: string;
  adminContinuationToken?: string;
  // Sort options
  sortBy: 'upvotes' | 'createdAt';
  sortOrder: 'asc' | 'desc';
  // Admin filter
  adminStatusFilter?: FeatureRequestStatus;
}

/**
 * Initial State
 */
const initialState: FeatureRequestState = {
  publicRequests: [],
  myRequests: [],
  adminRequests: [],
  selectedRequest: null,
  userVotes: {},
  isLoading: false,
  isSubmitting: false,
  isVoting: false,
  isUpdatingStatus: false,
  error: null,
  sortBy: 'upvotes',
  sortOrder: 'desc',
};

/**
 * Async Thunks
 */

// Fetch public (approved) feature requests
export const fetchPublicFeatureRequests = createAsyncThunk<
  { requests: FeatureRequest[]; votes: Record<string, 'up' | 'down'>; token?: string },
  | { sortBy?: 'upvotes' | 'createdAt'; sortOrder?: 'asc' | 'desc'; continuationToken?: string }
  | undefined,
  { rejectValue: ApiErrorState; state: RootState }
>('featureRequests/fetchPublic', async (params, { rejectWithValue, getState }) => {
  try {
    logger.debug('Fetching public feature requests', {
      event: 'feature_requests.fetch_public.started',
    });
    const state = getState();
    const { featureRequests, userVotes, continuationToken } = await featureRequestService.list({
      sortBy: params?.sortBy || state.featureRequests.sortBy,
      sortOrder: params?.sortOrder || state.featureRequests.sortOrder,
      continuationToken: params?.continuationToken,
    });
    logger.debug('Fetched public feature requests', {
      event: 'feature_requests.fetch_public.succeeded',
      count: featureRequests.length,
    });
    return { requests: featureRequests, votes: userVotes, token: continuationToken };
  } catch (err: any) {
    logger.error('Failed to fetch feature requests', err, {
      event: 'feature_requests.fetch_public.failed',
      code: err.code,
    });
    return rejectWithValue({
      message: err.message || 'Failed to fetch feature requests',
      code: err.code,
      timestamp: new Date().toISOString(),
    });
  }
});

// Fetch user's own feature requests
export const fetchMyFeatureRequests = createAsyncThunk<
  { requests: FeatureRequest[]; token?: string },
  { continuationToken?: string } | undefined,
  { rejectValue: ApiErrorState }
>('featureRequests/fetchMy', async (params, { rejectWithValue }) => {
  try {
    logger.debug('Fetching my feature requests', {
      event: 'feature_requests.fetch_my.started',
    });
    const { featureRequests, continuationToken } = await featureRequestService.listMyRequests({
      continuationToken: params?.continuationToken,
    });
    logger.debug('Fetched my feature requests', {
      event: 'feature_requests.fetch_my.succeeded',
      count: featureRequests.length,
    });
    return { requests: featureRequests, token: continuationToken };
  } catch (err: any) {
    logger.error('Failed to fetch your feature requests', err, {
      event: 'feature_requests.fetch_my.failed',
      code: err.code,
    });
    return rejectWithValue({
      message: err.message || 'Failed to fetch your feature requests',
      code: err.code,
      timestamp: new Date().toISOString(),
    });
  }
});

// Fetch single feature request
export const fetchFeatureRequestById = createAsyncThunk<
  { request: FeatureRequest; vote: 'up' | 'down' | null },
  string,
  { rejectValue: ApiErrorState }
>('featureRequests/fetchById', async (id, { rejectWithValue }) => {
  try {
    logger.debug('Fetching feature request', {
      event: 'feature_requests.fetch_by_id.started',
      featureRequestId: id,
    });
    const { featureRequest, userVote } = await featureRequestService.getById(id);
    logger.debug('Fetched feature request', {
      event: 'feature_requests.fetch_by_id.succeeded',
      featureRequestId: id,
    });
    return { request: featureRequest, vote: userVote };
  } catch (err: any) {
    logger.error('Failed to fetch feature request', err, {
      event: 'feature_requests.fetch_by_id.failed',
      featureRequestId: id,
      code: err.code,
    });
    return rejectWithValue({
      message: err.message || 'Failed to fetch feature request',
      code: err.code,
      timestamp: new Date().toISOString(),
    });
  }
});

// Submit new feature request
export const submitFeatureRequest = createAsyncThunk<
  FeatureRequest,
  CreateFeatureRequestRequest,
  { rejectValue: ApiErrorState }
>('featureRequests/submit', async (data, { rejectWithValue }) => {
  try {
    const created = await featureRequestService.create(data);
    logger.info('Feature request submitted', {
      event: 'feature_requests.submit.succeeded',
      featureRequestId: created.id,
    });
    return created;
  } catch (err: any) {
    logger.error('Failed to submit feature request', err, {
      event: 'feature_requests.submit.failed',
      code: err.code,
    });
    return rejectWithValue({
      message: err.message || 'Failed to submit feature request',
      code: err.code,
      timestamp: new Date().toISOString(),
    });
  }
});

// Vote on feature request
export const voteOnFeatureRequest = createAsyncThunk<
  { id: string; updatedRequest: FeatureRequest; voteType: 'up' | 'down' },
  { id: string; voteType: 'up' | 'down' },
  { rejectValue: ApiErrorState }
>('featureRequests/vote', async ({ id, voteType }, { rejectWithValue }) => {
  try {
    const updatedRequest = await featureRequestService.vote(id, voteType);
    logger.info('Feature request voted', {
      event: 'feature_requests.vote.succeeded',
      featureRequestId: id,
    });
    return { id, updatedRequest, voteType };
  } catch (err: any) {
    logger.error('Failed to vote on feature request', err, {
      event: 'feature_requests.vote.failed',
      featureRequestId: id,
      code: err.code,
    });
    return rejectWithValue({
      message: err.message || 'Failed to vote',
      code: err.code,
      timestamp: new Date().toISOString(),
    });
  }
});

// Remove vote
export const removeVoteFromFeatureRequest = createAsyncThunk<
  { id: string; updatedRequest: FeatureRequest },
  string,
  { rejectValue: ApiErrorState }
>('featureRequests/removeVote', async (id, { rejectWithValue }) => {
  try {
    const updatedRequest = await featureRequestService.removeVote(id);
    logger.info('Feature request vote removed', {
      event: 'feature_requests.remove_vote.succeeded',
      featureRequestId: id,
    });
    return { id, updatedRequest };
  } catch (err: any) {
    logger.error('Failed to remove vote', err, {
      event: 'feature_requests.remove_vote.failed',
      featureRequestId: id,
      code: err.code,
    });
    return rejectWithValue({
      message: err.message || 'Failed to remove vote',
      code: err.code,
      timestamp: new Date().toISOString(),
    });
  }
});

// Admin: Fetch all feature requests
export const adminFetchFeatureRequests = createAsyncThunk<
  { requests: FeatureRequest[]; token?: string },
  { status?: FeatureRequestStatus; continuationToken?: string } | undefined,
  { rejectValue: ApiErrorState }
>('featureRequests/adminFetch', async (params, { rejectWithValue }) => {
  try {
    logger.debug('Admin fetching feature requests', {
      event: 'feature_requests.admin_fetch.started',
    });
    const { featureRequests, continuationToken } = await featureRequestService.adminList({
      status: params?.status,
      continuationToken: params?.continuationToken,
    });
    logger.debug('Admin fetched feature requests', {
      event: 'feature_requests.admin_fetch.succeeded',
      count: featureRequests.length,
    });
    return { requests: featureRequests, token: continuationToken };
  } catch (err: any) {
    logger.error('Failed to admin-fetch feature requests', err, {
      event: 'feature_requests.admin_fetch.failed',
      code: err.code,
    });
    return rejectWithValue({
      message: err.message || 'Failed to fetch feature requests',
      code: err.code,
      timestamp: new Date().toISOString(),
    });
  }
});

// Admin: Update status
export const adminUpdateFeatureRequestStatus = createAsyncThunk<
  FeatureRequest,
  { id: string; data: UpdateFeatureRequestStatusRequest },
  { rejectValue: ApiErrorState }
>('featureRequests/adminUpdateStatus', async ({ id, data }, { rejectWithValue }) => {
  try {
    const updated = await featureRequestService.adminUpdateStatus(id, data);
    logger.info('Feature request status updated', {
      event: 'feature_requests.admin_update_status.succeeded',
      featureRequestId: id,
    });
    return updated;
  } catch (err: any) {
    logger.error('Failed to update feature request status', err, {
      event: 'feature_requests.admin_update_status.failed',
      featureRequestId: id,
      code: err.code,
    });
    return rejectWithValue({
      message: err.message || 'Failed to update status',
      code: err.code,
      timestamp: new Date().toISOString(),
    });
  }
});

// Admin: Respond
export const adminRespondToFeatureRequest = createAsyncThunk<
  FeatureRequest,
  { id: string; response: string },
  { rejectValue: ApiErrorState }
>('featureRequests/adminRespond', async ({ id, response }, { rejectWithValue }) => {
  try {
    const updated = await featureRequestService.adminRespond(id, response);
    logger.info('Feature request response added', {
      event: 'feature_requests.admin_respond.succeeded',
      featureRequestId: id,
    });
    return updated;
  } catch (err: any) {
    logger.error('Failed to respond to feature request', err, {
      event: 'feature_requests.admin_respond.failed',
      featureRequestId: id,
      code: err.code,
    });
    return rejectWithValue({
      message: err.message || 'Failed to add response',
      code: err.code,
      timestamp: new Date().toISOString(),
    });
  }
});

// Admin: Delete
export const adminDeleteFeatureRequest = createAsyncThunk<
  string,
  string,
  { rejectValue: ApiErrorState }
>('featureRequests/adminDelete', async (id, { rejectWithValue }) => {
  try {
    await featureRequestService.adminDelete(id);
    logger.info('Feature request deleted', {
      event: 'feature_requests.admin_delete.succeeded',
      featureRequestId: id,
    });
    return id;
  } catch (err: any) {
    logger.error('Failed to delete feature request', err, {
      event: 'feature_requests.admin_delete.failed',
      featureRequestId: id,
      code: err.code,
    });
    return rejectWithValue({
      message: err.message || 'Failed to delete',
      code: err.code,
      timestamp: new Date().toISOString(),
    });
  }
});

/**
 * Feature Request Slice
 */
const featureRequestSlice = createSlice({
  name: 'featureRequests',
  initialState,
  reducers: {
    setSelectedRequest: (state, action: PayloadAction<FeatureRequest | null>) => {
      state.selectedRequest = action.payload;
    },
    clearSelectedRequest: (state) => {
      state.selectedRequest = null;
    },
    setSortBy: (state, action: PayloadAction<'upvotes' | 'createdAt'>) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortOrder = action.payload;
    },
    setAdminStatusFilter: (state, action: PayloadAction<FeatureRequestStatus | undefined>) => {
      state.adminStatusFilter = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetFeatureRequestState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch public
      .addCase(fetchPublicFeatureRequests.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPublicFeatureRequests.fulfilled, (state, action) => {
        state.isLoading = false;
        state.publicRequests = action.payload.requests;
        state.userVotes = { ...state.userVotes, ...action.payload.votes };
        state.publicContinuationToken = action.payload.token;
      })
      .addCase(fetchPublicFeatureRequests.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? { message: 'Failed to fetch' };
      })

      // Fetch my requests
      .addCase(fetchMyFeatureRequests.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMyFeatureRequests.fulfilled, (state, action) => {
        state.isLoading = false;
        state.myRequests = action.payload.requests;
        state.myContinuationToken = action.payload.token;
      })
      .addCase(fetchMyFeatureRequests.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? { message: 'Failed to fetch' };
      })

      // Fetch by ID
      .addCase(fetchFeatureRequestById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFeatureRequestById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedRequest = action.payload.request;
        if (action.payload.vote) {
          state.userVotes[action.payload.request.id] = action.payload.vote;
        }
      })
      .addCase(fetchFeatureRequestById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? { message: 'Failed to fetch' };
      })

      // Submit
      .addCase(submitFeatureRequest.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(submitFeatureRequest.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.myRequests.unshift(action.payload);
      })
      .addCase(submitFeatureRequest.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload ?? { message: 'Failed to submit' };
      })

      // Vote
      .addCase(voteOnFeatureRequest.pending, (state) => {
        state.isVoting = true;
        state.error = null;
      })
      .addCase(voteOnFeatureRequest.fulfilled, (state, action) => {
        state.isVoting = false;
        state.userVotes[action.payload.id] = action.payload.voteType;
        // Update in public requests
        const publicIdx = state.publicRequests.findIndex((r) => r.id === action.payload.id);
        if (publicIdx !== -1) {
          state.publicRequests[publicIdx] = action.payload.updatedRequest;
        }
        if (state.selectedRequest?.id === action.payload.id) {
          state.selectedRequest = action.payload.updatedRequest;
        }
      })
      .addCase(voteOnFeatureRequest.rejected, (state, action) => {
        state.isVoting = false;
        state.error = action.payload ?? { message: 'Failed to vote' };
      })

      // Remove vote
      .addCase(removeVoteFromFeatureRequest.pending, (state) => {
        state.isVoting = true;
        state.error = null;
      })
      .addCase(removeVoteFromFeatureRequest.fulfilled, (state, action) => {
        state.isVoting = false;
        delete state.userVotes[action.payload.id];
        const publicIdx = state.publicRequests.findIndex((r) => r.id === action.payload.id);
        if (publicIdx !== -1) {
          state.publicRequests[publicIdx] = action.payload.updatedRequest;
        }
        if (state.selectedRequest?.id === action.payload.id) {
          state.selectedRequest = action.payload.updatedRequest;
        }
      })
      .addCase(removeVoteFromFeatureRequest.rejected, (state, action) => {
        state.isVoting = false;
        state.error = action.payload ?? { message: 'Failed to remove vote' };
      })

      // Admin fetch
      .addCase(adminFetchFeatureRequests.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(adminFetchFeatureRequests.fulfilled, (state, action) => {
        state.isLoading = false;
        state.adminRequests = action.payload.requests;
        state.adminContinuationToken = action.payload.token;
      })
      .addCase(adminFetchFeatureRequests.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? { message: 'Failed to fetch' };
      })

      // Admin update status
      .addCase(adminUpdateFeatureRequestStatus.pending, (state) => {
        state.isUpdatingStatus = true;
        state.error = null;
      })
      .addCase(adminUpdateFeatureRequestStatus.fulfilled, (state, action) => {
        state.isUpdatingStatus = false;
        const idx = state.adminRequests.findIndex((r) => r.id === action.payload.id);
        if (idx !== -1) {
          state.adminRequests[idx] = action.payload;
        }
        if (state.selectedRequest?.id === action.payload.id) {
          state.selectedRequest = action.payload;
        }
      })
      .addCase(adminUpdateFeatureRequestStatus.rejected, (state, action) => {
        state.isUpdatingStatus = false;
        state.error = action.payload ?? { message: 'Failed to update' };
      })

      // Admin respond
      .addCase(adminRespondToFeatureRequest.pending, (state) => {
        state.isUpdatingStatus = true;
        state.error = null;
      })
      .addCase(adminRespondToFeatureRequest.fulfilled, (state, action) => {
        state.isUpdatingStatus = false;
        const idx = state.adminRequests.findIndex((r) => r.id === action.payload.id);
        if (idx !== -1) {
          state.adminRequests[idx] = action.payload;
        }
        if (state.selectedRequest?.id === action.payload.id) {
          state.selectedRequest = action.payload;
        }
      })
      .addCase(adminRespondToFeatureRequest.rejected, (state, action) => {
        state.isUpdatingStatus = false;
        state.error = action.payload ?? { message: 'Failed to respond' };
      })

      // Admin delete
      .addCase(adminDeleteFeatureRequest.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(adminDeleteFeatureRequest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.adminRequests = state.adminRequests.filter((r) => r.id !== action.payload);
        if (state.selectedRequest?.id === action.payload) {
          state.selectedRequest = null;
        }
      })
      .addCase(adminDeleteFeatureRequest.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? { message: 'Failed to delete' };
      });
  },
});

/**
 * Selectors
 */
export const selectPublicFeatureRequests = (state: RootState) =>
  state.featureRequests.publicRequests;
export const selectMyFeatureRequests = (state: RootState) => state.featureRequests.myRequests;
export const selectAdminFeatureRequests = (state: RootState) => state.featureRequests.adminRequests;
export const selectSelectedFeatureRequest = (state: RootState) =>
  state.featureRequests.selectedRequest;
export const selectUserVotes = (state: RootState) => state.featureRequests.userVotes;
export const selectFeatureRequestIsLoading = (state: RootState) => state.featureRequests.isLoading;
export const selectFeatureRequestIsSubmitting = (state: RootState) =>
  state.featureRequests.isSubmitting;
export const selectFeatureRequestIsVoting = (state: RootState) => state.featureRequests.isVoting;
export const selectFeatureRequestError = (state: RootState) => state.featureRequests.error;
export const selectFeatureRequestSortBy = (state: RootState) => state.featureRequests.sortBy;
export const selectFeatureRequestSortOrder = (state: RootState) => state.featureRequests.sortOrder;

// Derived selectors
export const selectPendingFeatureRequests = (state: RootState) =>
  state.featureRequests.adminRequests.filter((r) => r.status === 'pending');

export const selectApprovedFeatureRequests = (state: RootState) =>
  state.featureRequests.adminRequests.filter((r) =>
    ['approved', 'in_progress', 'completed'].includes(r.status)
  );

export const selectUserVoteForRequest = (state: RootState, requestId: string) =>
  state.featureRequests.userVotes[requestId];

/**
 * Export actions and reducer
 */
export const {
  setSelectedRequest,
  clearSelectedRequest,
  setSortBy,
  setSortOrder,
  setAdminStatusFilter,
  clearError,
  resetFeatureRequestState,
} = featureRequestSlice.actions;

export default featureRequestSlice.reducer;
